/**
 * Toolbar for the Rules page.
 *
 * Sits between the tabs and the table. Left side: search pill
 * (same white 300px pill shape used on Integration transactions,
 * for cross-page consistency). Right side: Customize gear icon
 * that toggles the CustomizeDrawer.
 *
 * Pure controlled component: receives searchValue and
 * onSearchChange from the parent, and onCustomizeToggle +
 * isCustomizeOpen for the gear button. No internal state.
 *
 * Mobile: search is full-width minus the gear button; both stay
 * visible so the Customize drawer is reachable on phones.
 */

import type { CSSProperties, ChangeEvent } from "react";
import { Search, Settings as SettingsIcon } from "lucide-react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
};

interface RulesToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCustomizeToggle: () => void;
  isCustomizeOpen: boolean;
}

export function RulesToolbar({
  searchValue,
  onSearchChange,
  onCustomizeToggle,
  isCustomizeOpen,
}: RulesToolbarProps) {
  const { isMobile } = useBreakpoint();

  return (
    <div style={isMobile ? wrapStyleMobile : wrapStyle}>
      <div style={leftStyle}>
        <div style={isMobile ? searchWrapMobile : searchWrap}>
          <Search
            size={15}
            color={PALETTE.ink4}
            strokeWidth={2}
            style={searchIconStyle}
          />
          <input
            type="text"
            placeholder="Search by name or conditions"
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onSearchChange(e.target.value)
            }
            style={searchInputStyle}
            aria-label="Search rules"
          />
        </div>
      </div>

      <div style={rightStyle}>
        <button
          type="button"
          onClick={onCustomizeToggle}
          aria-label="Customize table"
          aria-expanded={isCustomizeOpen}
          style={{
            ...iconBtnStyle,
            ...(isCustomizeOpen ? iconBtnActiveStyle : {}),
          }}
          title="Customize"
        >
          <SettingsIcon
            size={16}
            color={isCustomizeOpen ? PALETTE.ink : PALETTE.ink3}
            strokeWidth={2}
          />
        </button>
      </div>
    </div>
  );
}

const wrapStyle: CSSProperties = {
  padding: "14px 32px",
  display: "flex",
  gap: 10,
  alignItems: "center",
  justifyContent: "space-between",
  background: "#ffffff",
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  width: "100%",
};

const wrapStyleMobile: CSSProperties = {
  ...wrapStyle,
  padding: "10px 16px",
  gap: 8,
};

const leftStyle: CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  flex: 1,
  minWidth: 0,
};

const rightStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexShrink: 0,
};

const searchWrap: CSSProperties = {
  position: "relative",
  width: 300,
  maxWidth: "100%",
  flexShrink: 0,
};

const searchWrapMobile: CSSProperties = {
  ...searchWrap,
  width: "100%",
};

const searchIconStyle: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: 14,
  transform: "translateY(-50%)",
  pointerEvents: "none",
};

const searchInputStyle: CSSProperties = {
  width: "100%",
  padding: "9px 16px 9px 38px",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 500,
  color: PALETTE.ink,
  background: "#ffffff",
  outline: "none",
  fontFamily: "inherit",
  boxShadow: "none",
};

const iconBtnStyle: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 999,
  background: "transparent",
  border: `1px solid ${PALETTE.line}`,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
  transition: "background 0.12s, border-color 0.12s",
};

const iconBtnActiveStyle: CSSProperties = {
  background: PALETTE.bgFade,
  borderColor: PALETTE.ink,
};